import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_ROOT } from "../service/Services";

/** Professional email signature template (table-based, inline styles for email clients). Use {{logoUrl}} for logo. */
const SAMPLE_SIGNATURE_HTML = `<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #333333; max-width: 420px; line-height: 1.4;">
  <tr>
    <td style="padding-bottom: 14px; border-bottom: 2px solid #2563eb;">
      <img src="{{logoUrl}}" alt="Logo" style="max-height: 44px; width: auto; display: block;" />
    </td>
  </tr>
  <tr>
    <td style="padding-top: 14px;">
      <strong style="font-size: 16px; color: #111827; letter-spacing: 0.02em;">Anjali Sharma</strong><br/>
      <span style="color: #4b5563; font-size: 13px;">Senior Manager, Business Development</span><br/>
      <span style="color: #6b7280; font-size: 13px;">Zaptas Technologies</span>
    </td>
  </tr>
  <tr>
    <td style="padding-top: 12px; font-size: 12px; color: #6b7280;">
      <span style="color: #9ca3af;">|</span> &nbsp;<a href="tel:+919876543210" style="color: #2563eb; text-decoration: none;">+91 98765 43210</a> &nbsp;<span style="color: #9ca3af;">|</span> &nbsp;<a href="mailto:anjali@zaptas.com" style="color: #2563eb; text-decoration: none;">anjali@zaptas.com</a> &nbsp;<span style="color: #9ca3af;">|</span> &nbsp;<a href="https://www.zaptas.com" style="color: #2563eb; text-decoration: none;">www.zaptas.com</a>
    </td>
  </tr>
  <tr>
    <td style="padding-top: 10px; font-size: 11px; color: #9ca3af; font-style: italic;">
      Best regards,<br/>Anjali
    </td>
  </tr>
</table>`;

export default function Signatures() {
  const authHeaders = useMemo(
    () => ({ headers: { authorization: `Bearer ${localStorage.getItem("token")}` } }),
    []
  );

  const [signatures, setSignatures] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingSignature, setEditingSignature] = useState(null);
  const [form, setForm] = useState({
    name: "",
    content: "",
    logoUrl: "",
    isDefault: false,
    order: 0,
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchSignatures();
  }, []);

  const fetchSignatures = async () => {
    try {
      const res = await axios.get(`${API_ROOT}/mail-signatures`, authHeaders);
      if (res.data?.success) setSignatures(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const openAdd = () => {
    setEditingSignature(null);
    setForm({
      name: "",
      content: "",
      logoUrl: "",
      isDefault: false,
      order: signatures.length,
    });
    setShowModal(true);
  };

  const openAddWithSample = () => {
    setEditingSignature(null);
    setForm({
      name: "Company signature",
      content: SAMPLE_SIGNATURE_HTML,
      logoUrl: "",
      isDefault: false,
      order: signatures.length,
    });
    setShowModal(true);
  };

  const openEdit = (sig) => {
    setEditingSignature(sig);
    setForm({
      name: sig.name || "",
      content: sig.content || "",
      logoUrl: sig.logoUrl || "",
      isDefault: sig.isDefault === true,
      order: sig.order ?? 0,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!(form.name || "").trim()) {
      alert("Name is required");
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        content: form.content || "",
        logoUrl: (form.logoUrl || "").trim(),
        isDefault: form.isDefault,
        order: form.order,
      };
      if (editingSignature) {
        await axios.put(`${API_ROOT}/mail-signatures/${editingSignature._id}`, payload, authHeaders);
      } else {
        await axios.post(`${API_ROOT}/mail-signatures`, payload, authHeaders);
      }
      setShowModal(false);
      fetchSignatures();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save");
    }
  };

  const deleteSignature = async (id) => {
    if (!window.confirm("Delete this signature?")) return;
    try {
      await axios.delete(`${API_ROOT}/mail-signatures/${id}`, authHeaders);
      fetchSignatures();
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed");
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(jpeg|jpg|png|gif|webp|svg\+xml)/i.test(file.type)) {
      alert("Please choose an image (JPEG, PNG, GIF, WebP, or SVG).");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert("Image must be under 2 MB.");
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);
      const res = await axios.post(`${API_ROOT}/upload-signature-logo`, formData, {
        ...authHeaders,
        headers: { ...authHeaders.headers, "Content-Type": "multipart/form-data" },
      });
      if (res.data?.url) setForm((f) => ({ ...f, logoUrl: res.data.url }));
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Upload failed");
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const previewHtml = (form.content || "").replace(/\{\{logoUrl\}\}/gi, form.logoUrl || "");

  return (
    <div className="container mx-auto mt-8 p-6 bg-white rounded-xl shadow-sm">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">Signatures</h2>
      <p className="text-sm text-gray-500 mb-4">
        Email signatures (HTML + optional logo) are appended to campaign emails. Use <code className="bg-gray-100 px-1 rounded">{`{{logoUrl}}`}</code> in content to insert the logo URL.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">All signatures</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openAddWithSample}
            className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 border border-slate-300"
          >
            Use sample template
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700"
          >
            + Add signature
          </button>
        </div>
      </div>

      {signatures.length === 0 ? (
        <p className="text-gray-500">No signatures. Add one to append to campaign emails.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm border-collapse">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-3 py-2 text-left">Name</th>
                <th className="border px-3 py-2 text-left">Logo URL</th>
                <th className="border px-3 py-2 text-left">Default</th>
                <th className="border px-3 py-2 text-left">Order</th>
                <th className="border px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {signatures.map((sig) => (
                <tr key={sig._id} className="bg-white hover:bg-gray-50">
                  <td className="border px-3 py-2 font-medium">{sig.name}</td>
                  <td className="border px-3 py-2 text-gray-600 truncate max-w-xs" title={sig.logoUrl}>{sig.logoUrl || "—"}</td>
                  <td className="border px-3 py-2">{sig.isDefault ? "Yes" : "No"}</td>
                  <td className="border px-3 py-2">{sig.order}</td>
                  <td className="border px-3 py-2">
                    <button type="button" onClick={() => openEdit(sig)} className="text-blue-600 hover:underline mr-2">Edit</button>
                    <button type="button" onClick={() => deleteSignature(sig._id)} className="text-red-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-semibold mb-4">{editingSignature ? "Edit signature" : "Add signature"}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Company signature"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo (optional)</label>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="cursor-pointer px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 border border-gray-300">
                    {uploadingLogo ? "Uploading…" : "Upload logo"}
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                    />
                  </label>
                  <span className="text-xs text-gray-500">or paste URL below</span>
                </div>
                <input
                  type="url"
                  value={form.logoUrl}
                  onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
                  placeholder="https://example.com/logo.png or upload above"
                  className="mt-2 w-full px-3 py-2 border rounded-md"
                />
                {form.logoUrl && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">Current logo:</span>
                    <img src={form.logoUrl} alt="Logo" className="h-10 object-contain border rounded bg-gray-50" onError={(e) => { e.target.style.display = "none"; }} />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content (HTML)</label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder='e.g. <p>Best regards,<br/>Team</p> or use {{logoUrl}} for logo'
                  rows={6}
                  className="w-full px-3 py-2 border rounded-md font-mono text-sm"
                />
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Preview (exact content as in email)</p>
                <div
                  className="min-h-[80px] rounded border border-gray-200 bg-white p-4 text-left prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: previewHtml || "<span class=\"text-gray-400\">Enter content above to see preview.</span>" }}
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.isDefault}
                    onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                    className="rounded"
                  />
                  Default signature
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Order</label>
                  <input
                    type="number"
                    min={0}
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })}
                    className="w-20 px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200">Cancel</button>
              <button type="button" onClick={save} className="px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
